/**
 * Page transitions. A black curtain rises over the current page, the route
 * changes underneath it, and the curtain lifts away. It shares its easing
 * and colour with the loader, so leaving a page feels like the site
 * "re-opening", not like a link click.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { V5Context, useV5 } from "../lib/context";
import { prefersReducedMotion } from "../lib/hooks";

const COVER_MS = 650;
const LIFT_MS = 800;

export function TLink({ to, label, className, children, onClick, ...rest }) {
  const { go } = useV5();
  return (
    <a
      href={to}
      className={className}
      {...rest}
      onClick={(e) => {
        if (onClick) onClick(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        go(to, label);
      }}>
      {children}
    </a>
  );
}

export default function TransitionProvider({ ready, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState("idle"); // idle | cover | lift
  const [label, setLabel] = useState("");
  const busy = useRef(false);

  const go = useCallback((to, text = "") => {
    if (busy.current) return;
    if (to === location.pathname) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (prefersReducedMotion()) { navigate(to); window.scrollTo(0, 0); return; }
    busy.current = true;
    setLabel(text);
    setPhase("cover");
    setTimeout(() => {
      navigate(to);
      window.scrollTo(0, 0);
      setPhase("lift");
      setTimeout(() => { setPhase("idle"); busy.current = false; }, LIFT_MS);
    }, COVER_MS);
  }, [navigate, location.pathname]);

  const value = useMemo(() => ({ ready, go }), [ready, go]);

  return (
    <V5Context.Provider value={value}>
      {children}
      <div className={`v5-curtain is-${phase}`} aria-hidden="true">
        <span className="v5-curtain-label">{label}</span>
      </div>
    </V5Context.Provider>
  );
}
